
import { Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ApplicationConfiguration, ApplicationService, ApplicationSettings, IConfigFilter } from '@services/application.service';
import { filter, firstValueFrom } from 'rxjs';
import { defAppName, GlobalFunctions } from '@global/GlobalFunctions';
import { Router } from '@angular/router';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from '@environments/environment';

@Component({
	selector: 'app-maintenance',
	templateUrl: './maintenance.component.html',
	styleUrl: './maintenance.component.scss'
})
export class MaintenanceComponent implements OnInit, OnDestroy {

	/** Application Configuration */
	public appConfiguration: ApplicationConfiguration ;

	/** Environment Variables */
	public env: any

	/** Observable Subject Finalise */
	private destroyRef = inject(DestroyRef);

	/** App Name */
	public sAppName: SafeHtml; // string = defAppName;

	/** Component destruction state */
	private isDestroyed = false;

	/** Poll Timer */
	private pollTimer: ReturnType<typeof setTimeout> | null = null;

	/** Poll interval in milliseconds */
	private pollMS: number = 1 * 60 * 1000;

	/**
	 * constructor
	 * @param applicationService
	 * @param sanitizer
	 * @param router
	 * @param siteSubscriptions
	 */
	constructor(
		private applicationService: ApplicationService,
		private sanitizer: DomSanitizer,
		private router: Router,
		private siteSubscriptions: siteSubscriptions,
		private globalFunctions: GlobalFunctions
	) {
		this.sAppName = this.sanitizeAppName(defAppName);
		this.env = environment;
	}

	/**
	 * sanitizeAppName
	 * @param appName
	 * @returns
	 */
	sanitizeAppName(appName: string): SafeHtml {
		return this.sanitizer.bypassSecurityTrustHtml(appName);
	}

	/**
	 * ngOnInit
	 */
	async ngOnInit() {
		this.subscribeSettings();
		await this.CheckAppConfig();

	}

	/**
	 * ngOnDestroy
	 */
	ngOnDestroy(): void {
		this.isDestroyed = true;

		/** Cancel any pending configuration poll */
		if (this.pollTimer) {
			clearTimeout(this.pollTimer);
			this.pollTimer = null;
		}
	}

	/**
	 * isMaintenanceEnded
	 * @returns 
	 */
	isMaintenanceEnded(): boolean {
		return this.appConfiguration?.maintenance?.end != null &&
			this.appConfiguration.maintenance.end <= Date.now();
	}

	/**
	 * subscribeSettings
	 */
	subscribeSettings() {
		this.siteSubscriptions.applicationSettings$.pipe(
			takeUntilDestroyed(this.destroyRef),
			filter((result) => !!result && Object.keys(result).length > 0 && !this.isDestroyed)
		).subscribe((appSettings: ApplicationSettings) => {
			try {
				this.appConfiguration = appSettings.Config;

				var inMaintenance: boolean = false;

				if (Object.keys(appSettings).length > 0) {
					if (this.appConfiguration.maintenance.enabled &&
						this.appConfiguration.maintenance.start != null) {

						var epoch: number = Date.now();

						if (epoch >= this.appConfiguration.maintenance.start
							// &&
							// (this.configuration.maintenance.end == null ||
							// 	epoch < this.configuration.maintenance.end)
						) {

							inMaintenance = true;
						}
					}
				}

				if (!inMaintenance) {
					this.router.navigateByUrl('/');
				}
			}
			finally { }
		})
	}

	/**
	 * CheckAppConfig
	 */
	async CheckAppConfig() {

		if (this.isDestroyed) { return; }

		var inMaintenance = false;

		try {
			const cfgFilter = <IConfigFilter>{
				application: this.env.appName,
				environment: this.env.production ? 'Production' : "Development"
			}
			const result = await firstValueFrom(
				this.applicationService.getSettings$(cfgFilter)
			);

			if (this.isDestroyed) { return; }

			var appSettings = new ApplicationSettings(JSON.parse(result[0].Data));
			this.appConfiguration = appSettings.Config;
			const maintenance = this.appConfiguration.maintenance;
			const now = Date.now();

			inMaintenance =
				maintenance.enabled &&
				maintenance.start != null &&
				now >= maintenance.start
				// && (maintenance.end == null || now < maintenance.end)
				;

			if (!inMaintenance) {
				this.isDestroyed = true;
				this.siteSubscriptions.setApplicationSettings(appSettings, true);
				this.router.navigateByUrl('/');
				return;
			}

		} finally {
			if (inMaintenance && !this.isDestroyed) {

				if (this.pollTimer) {
					clearTimeout(this.pollTimer);
				}

				this.pollTimer = setTimeout(() => {
					console.log("maintenance.component: this.pollTimer")
					this.pollTimer = null;
					this.CheckAppConfig();
				}, this.pollMS);
			}
		}
	}


	/**
	 * recheckClick
	 */
	async recheckClick() {

		const cfgFilter = <IConfigFilter>{
			application: this.env.appName,
			environment: this.env.production ? 'Production' : "Development"
		}
		const result = await firstValueFrom(
			this.applicationService.getSettings$(cfgFilter)
		);
		if (this.isDestroyed) { return; }
		var appSettings = new ApplicationSettings(JSON.parse(result[0].Data));
		const maintenance = appSettings.Config.maintenance;
		if (maintenance.enabled) {
			const OKResult = await firstValueFrom(
				this.globalFunctions.UserConfirm$(
					"Maintenance is still enabled",
					"Checking"
				)
			);
		}
	}
}
